import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import { localISODate } from '../utils/dates';
import { errorMessage, num, period, shortAmount, PERIODS, monthLabel, downloadCsv } from '../utils/reportHelpers';
import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';

const COLORS = {
  light: { spend: '#6f42c1', grid: '#e9ecef', axis: '#6c757d' },
  dark: { spend: '#a98eda', grid: '#3a3a38', axis: '#adb5bd' }
};

const quantityText = (r) => (r.quantity ? `${Number(r.quantity).toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${r.unit || ''}`.trim() : '-');

/**
 * Purchase > Reports (managers): what was bought in a period (before GST, less purchase returns), month by month, by vendor and by item with
 * how each item's price has moved since the buy before, and the purchase orders still open as of today.
 */
function PurchaseReports({ active, currency = 'INR' }) {
  const { theme } = useTheme();
  const colors = COLORS[theme] || COLORS.light;
  const [preset, setPreset] = useState('thisFy');
  const [range, setRange] = useState(() => period('thisFy').map(localISODate));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('vendors');

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 });
    return (v) => f.format(num(v));
  }, [currency]);
  const rate = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 });
    return (v) => f.format(num(v));
  }, [currency]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await axios.get('/api/reports/purchases', { params: { from: range[0], to: range[1] } });
      setData(r);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not work out the report'));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { if (active) load(); }, [active, load]);

  if (!active) return null;

  const choose = (key) => {
    setPreset(key);
    setRange(period(key).map(localISODate));
  };
  const t = data?.totals;
  const open = data?.openOrders;
  const rows = data && view !== 'orders' ? data[view] : [];
  const percent = (v) => (v == null ? '-' : `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`);

  const change = (r) => {
    if (r.change == null) return <span className="text-muted">-</span>;
    const up = num(r.change) > 0;
    // A price that went up is bad news for a buyer, one that came down is good
    return <span className={num(r.change) === 0 ? 'text-muted' : up ? 'text-danger' : 'text-success'}>
      {up ? '▲' : num(r.change) < 0 ? '▼' : ''} {percent(Math.abs(num(r.change)))}
    </span>;
  };

  const exportRows = () => {
    if (view === 'vendors') {
      downloadCsv(`purchases-by-vendor-${range[0]}-to-${range[1]}.csv`,
        ['Vendor', 'Spend (before GST)', 'Share %', 'Returns', 'Bills', 'Last bill'],
        rows.map((r) => [r.name, r.spend, r.share, r.returned, r.bills, r.lastDate]));
    } else if (view === 'items') {
      downloadCsv(`purchases-by-item-${range[0]}-to-${range[1]}.csv`,
        ['Item', 'Quantity', 'Unit', 'Spend (before GST)', 'Average rate', 'Last rate', 'Previous rate', 'Change %', 'Last vendor', 'Last bought'],
        rows.map((r) => [r.name, r.quantity, r.unit, r.spend, r.averageRate, r.lastRate, r.previousRate, r.change, r.lastVendor, r.lastDate]));
    } else {
      downloadCsv('open-purchase-orders.csv',
        ['PO', 'PO date', 'Wanted by', 'Vendors', 'Ordered', 'Still to receive', 'Days late'],
        (open?.orders || []).map((o) => [o.poNumber, o.poDate, o.requiredDate, (o.vendors || []).join('; '), o.ordered, o.pending, o.daysLate]));
    }
  };
  const nothing = view === 'orders' ? !(open?.orders || []).length : rows.length === 0;

  return (
    <div className="mt-3">
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <ButtonGroup size="sm">
          {PERIODS.map(([key, label]) => (
            <Button key={key} variant={preset === key ? 'secondary' : 'outline-secondary'} onClick={() => choose(key)}>{label}</Button>
          ))}
        </ButtonGroup>
        <div className="d-flex align-items-center gap-1 ms-md-2">
          <DateInput size="sm" value={range[0]} max={range[1]} style={{ width: 'auto' }}
            onChange={(e) => { setPreset(''); setRange([e.target.value, range[1]]); }} />
          <span className="small text-muted">to</span>
          <DateInput size="sm" value={range[1]} min={range[0]} style={{ width: 'auto' }}
            onChange={(e) => { setPreset(''); setRange([range[0], e.target.value]); }} />
        </div>
        {loading && <Spinner animation="border" size="sm" />}
      </div>

      {t && (
        <>
          <Row className="g-3 mb-3">
            {[
              ['Spend (before GST)', money(t.spend)],
              ['GST on it', money(t.gst)],
              ['Bills', `${t.bills}${t.averageBill ? ` · avg ${money(t.averageBill)}` : ''}`],
              ['Returned to vendors', `${money(t.returns)}${t.returnNotes ? ` · ${t.returnNotes}` : ''}`],
              ['Vendors', t.vendors]
            ].map(([label, value]) => (
              <Col key={label} xs={6} md>
                <Card className="text-center h-100"><Card.Body className="py-2">
                  <div className="fs-5 fw-semibold">{value}</div>
                  <div className="small text-muted">{label}</div>
                </Card.Body></Card>
              </Col>
            ))}
          </Row>

          {data.months.length > 1 && (
            <Card className="mb-3">
              <Card.Header className="py-2"><strong>Month by month</strong></Card.Header>
              <Card.Body style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.months.map((m) => ({ ...m, label: monthLabel(m.key), spend: num(m.spend) }))} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.axis }} tickLine={false} axisLine={{ stroke: colors.grid }} />
                    <YAxis tick={{ fontSize: 12, fill: colors.axis }} axisLine={false} tickLine={false} width={70} tickFormatter={shortAmount} />
                    <Tooltip formatter={(v) => [money(v), 'Spend']} />
                    <Bar dataKey="spend" fill={colors.spend} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          )}

          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <ButtonGroup size="sm">
              <Button variant={view === 'vendors' ? 'primary' : 'outline-primary'} onClick={() => setView('vendors')}>By vendor</Button>
              <Button variant={view === 'items' ? 'primary' : 'outline-primary'} onClick={() => setView('items')}>By item</Button>
              <Button variant={view === 'orders' ? 'primary' : 'outline-primary'} onClick={() => setView('orders')}>
                Open orders{open?.count ? ` (${open.count})` : ''}
              </Button>
            </ButtonGroup>
            <Button size="sm" variant="outline-secondary" className="ms-auto" disabled={nothing} onClick={exportRows}>
              <i className="bi bi-download me-1" />Download CSV
            </Button>
          </div>

          {view === 'orders' && open && open.count > 0 && (
            <Alert variant={open.late > 0 ? 'warning' : 'light'} className="small py-2">
              {open.count} approved purchase order{open.count === 1 ? ' is' : 's are'} not fully received: {money(open.pending)} still to come
              {open.late > 0 ? `, ${open.late} past the date it was wanted` : ''}. This is as of today, whatever period is chosen.
            </Alert>
          )}

          <Table striped bordered hover responsive size="sm" className="small">
            <thead>
              {view === 'vendors' && (
                <tr><th>Vendor</th><th className="text-end">Spend</th><th className="text-end">Share</th><th className="text-end">Returns</th>
                  <th className="text-end">Bills</th><th>Last bill</th></tr>
              )}
              {view === 'items' && (
                <tr><th>Item</th><th className="text-end">Quantity</th><th className="text-end">Spend</th><th className="text-end">Avg rate</th>
                  <th className="text-end">Last rate</th><th className="text-end" title="Last rate against the buy before it">Change</th><th>Last bought from</th></tr>
              )}
              {view === 'orders' && (
                <tr><th>PO</th><th>Date</th><th>Wanted by</th><th>Vendors</th><th className="text-end">Ordered</th><th className="text-end">Still to receive</th></tr>
              )}
            </thead>
            <tbody>
              {nothing && (
                <tr><td colSpan={7} className="text-center text-muted py-3">
                  {view === 'orders' ? 'No purchase order is waiting for goods.' : 'No purchases in this period.'}
                </td></tr>
              )}
              {view === 'vendors' && rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.name}</td>
                  <td className="text-end text-nowrap">{money(r.spend)}</td>
                  <td className="text-end">{percent(r.share)}</td>
                  <td className="text-end text-nowrap">{num(r.returned) > 0 ? money(r.returned) : '-'}</td>
                  <td className="text-end">{r.bills}</td>
                  <td className="text-nowrap">{formatDate(r.lastDate) || '-'}</td>
                </tr>
              ))}
              {view === 'items' && rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.name}</td>
                  <td className="text-end text-nowrap">{quantityText(r)}</td>
                  <td className="text-end text-nowrap">{money(r.spend)}</td>
                  <td className="text-end text-nowrap">{r.averageRate != null ? rate(r.averageRate) : '-'}</td>
                  <td className="text-end text-nowrap">{r.lastRate != null ? rate(r.lastRate) : '-'}</td>
                  <td className="text-end text-nowrap">{change(r)}</td>
                  <td>{r.lastVendor || '-'}</td>
                </tr>
              ))}
              {view === 'orders' && (open?.orders || []).map((o) => (
                <tr key={o.id}>
                  <td>{o.poNumber}</td>
                  <td className="text-nowrap">{formatDate(o.poDate) || '-'}</td>
                  <td className="text-nowrap">
                    {formatDate(o.requiredDate) || '-'}
                    {o.late && <Badge bg="danger" className="ms-2">{o.daysLate} day{o.daysLate === 1 ? '' : 's'} late</Badge>}
                  </td>
                  <td>{(o.vendors || []).join(', ') || '-'}</td>
                  <td className="text-end text-nowrap">{money(o.ordered)}</td>
                  <td className="text-end text-nowrap">{money(o.pending)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="small text-muted">
            Spend is before GST, by the vendor&apos;s own bill date, net of purchase returns; cancelled bills are left out. An item&apos;s rate is its value
            over its quantity on each bill line; the change compares the latest buy with the one before it, whoever the vendor was, and a rise is
            shown in red. Freight, services and expenses are together under &ldquo;Services and expenses&rdquo;.
          </p>
        </>
      )}
    </div>
  );
}

export default PurchaseReports;
